import { ErrorHandler, inject, Service } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { MBMError } from './mbm-error';
import { ClientErrorLogService } from './client-error-log.service';
import { ErrorMessageService } from './error-message.service';

@Service({ autoProvided: false })
export class GlobalErrorHandler implements ErrorHandler {
  private readonly errorLogger = inject(ClientErrorLogService);
  private readonly errorMessageService = inject(ErrorMessageService);
  private readonly translate = inject(TranslateService);

  handleError(error: unknown): void {
    const mbmError = error as MBMError;
    const isOriginalErrorUnhandled = !mbmError.originalError || !(mbmError.originalError as MBMError).handled;
    const isErrorUnhandled = !mbmError.handled;
    const isUnhandled = isOriginalErrorUnhandled && isErrorUnhandled;

    // Opaque cross-origin "Script error." events carry no actionable detail
    // (WebKit strips it for security). iOS Safari fires one when the native
    // Share sheet opens, which produced a false global error toast. Suppress
    // only the user-facing message for these — still log them so genuine
    // cross-origin failures remain captured on the server.
    const isOpaqueCrossOriginError = this.isOpaqueCrossOriginError(error);

    if (isUnhandled && !isOpaqueCrossOriginError) {
      if (mbmError.statusCode === 403) {
        void this.errorMessageService.displayErrorMessage('Error', this.translate.instant('errors.forbidden'));
      } else {
        void this.errorMessageService.displayGlobalErrorMessage();
      }
      console.error('[GlobalErrorHandler] Unhandled error:', error);
    }

    if (isUnhandled || mbmError.logError === true) {
      void this.errorLogger.logErrorAsync(error).then(() => {
        console.log('[GlobalErrorHandler] Error logged to server');
      });
    }
  }

  private isOpaqueCrossOriginError(error: unknown): boolean {
    const message = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
    return message.includes('Script error') || message.includes('An ErrorEvent with no error occurred');
  }
}
